/**
 * StoreListContainer
 * --
 */
import React, { useEffect, useState } from 'react';
import StoreListPresenter from './StoreListPresenter';
import { useGetStores } from 'hooks/ShopInformationHooks';
import { useGetStoreAdmins } from 'hooks/AdminHooks';
import { useGetOrders } from 'hooks/OrderHooks';
import { useCreateMenu, useGetMenus, useUpdateMenu } from 'hooks/MenuHooks';
import {
  useCreateStaff,
  useGetStaffs,
  useUpdateStaff,
  useDeleteStaff,
} from 'hooks/StaffHooks';
import {
  useCreateProductCategory,
  useGetMenuCategories,
  useUpdateProductCategory,
} from 'hooks/ProductCategoryHooks';
import { useFileUpload } from 'hooks/FileHooks';
import { useCreateShopInformationFile } from 'hooks/ShopInformationFileHooks';
import { useUpdateShopInformation } from 'hooks/ShopInformationHooks';
import { useGetShopMenus } from 'hooks/ShopMenuHooks';
import MessageAlert, { STORE_URL } from 'utils';
import { LoadingPage } from 'components';
import Formatter from 'utils/Formatter';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const today = moment().format('YYYY-MM-DD');

/**
 *
 * --
 */
const StoreListContainer = () => {
  /* ===== Route ===== */
  const navigate = useNavigate();

  /* ===== Props ===== */
  /* ===== Mutate ===== */
  const { createStaff } = useCreateStaff();
  const { updateStaff } = useUpdateStaff();
  const { deleteStaff } = useDeleteStaff();
  const { createMenu } = useCreateMenu();
  const { updateMenu } = useUpdateMenu();
  const { createProductCategory } = useCreateProductCategory();
  const { updateProductCategory } = useUpdateProductCategory();
  const { updateShopInformation } = useUpdateShopInformation();
  const { createShopInformationFile } = useCreateShopInformationFile();
  const { fileUpload } = useFileUpload();
  /* ===== Data ===== */
  // 스토어 전체 조회
  const { data: Stores, refetch } = useGetStores({
    join_tables: '["shop_information_file"]',
  });
  // 메뉴 전체 조회
  const { data: Menus } = useGetMenus({
    join_tables: '["menu_file"]',
  });

  // 메뉴 전체 조회
  const { data: Orders } = useGetOrders({
    // FIXME: 날짜별 필터 필수
    // start_date: moment().startOf('month').unix(),
    // end_date: moment().endOf('month').unix(),
  });

  // 매장 관리자 전체 조회
  const { data: Admins } = useGetStoreAdmins();
  // 매장 직원 전체 조회
  const { data: Staffs, refetch: refetchStaffs } = useGetStaffs();
  // 메뉴 카테고리 전체 조회
  const { data: Categories } = useGetMenuCategories();
  // 매장 메뉴 전체 조회
  const { data: ShopMenus } = useGetShopMenus();

  /* ===== State ===== */
  // 로딩
  const [isLoading, setIsLoading] = useState(false);
  // 정제된 데이터
  const [data, setData] = React.useState([]);
  const [categoryData, setCategoryData] = React.useState([]);
  const [selectday, setSelectday] = useState(null);
  // 메뉴 매출 데이터
  const [menuSales, setMenuSales] = useState([]);

  // 일별 매출 상태
  const [dailySales, setDailySales] = useState({});
  const [dailyRefundSales, setDailyRefundSales] = useState({});
  const [dailyCouponSales, setDailyCouponSales] = useState({});
  // 매장 위도/경도
  const [coords, setCoords] = useState({
    latitude: 0, // 위도
    longitude: 0, // 경도
  });
  // 탭 아이템
  const [tabItems, setTabItems] = React.useState([]);
  // 모달
  const [isOpened, setIsOpened] = React.useState(false);
  const [modalType, setModalType] = React.useState('');
  const [modalData, setModalData] = React.useState({
    menu: {},
    category: {},
    staff: {},
    store: {},
  });
  // 스토어 선택
  const [selectedStore, setSelectedStore] = useState(null);
  // 리패칭
  const [isRefetch, setIsRefetch] = useState(false);

  // 매장 파일 정보
  const [menuFiles, setMenuFiles] = React.useState([]);
  const [thumbnailFiles, setThumbnailFiles] = React.useState([]);
  const [storeFiles, setStoreFiles] = React.useState([]);
  const [licenseFiles, setLicenseFiles] = React.useState([]);
  const [etcFiles, setEtcFiles] = React.useState([]);

  /* ===== Variables ===== */
  /* ===== Function ===== */

  const createCategoryMap = (categories) => {
    return categories?.reduce((acc, category) => {
      acc[category?.product_category_id] = category?.product_category_name;
      return acc;
    }, {});
  };

  const getSortedMenuData = (orders) => {
    const newOrders = orders?.filter((item) => {
      // 기본 조건: payment_state가 'COMPLETED'인 경우
      const isPaymentCompleted = item?.payment_state === 'COMPLETED';

      // 선택된 날짜가 있을 경우 날짜 필터링 추가
      const isDateMatching = selectday
        ? moment.unix(item.created_at).format('YYYY-MM-DD') === selectday
        : true;

      return isPaymentCompleted && isDateMatching;
    });
    // 메뉴 이름별 판매 수량 합산 및 ID, 가격 유지
    const menuSales =
      newOrders &&
      newOrders.reduce((acc, order) => {
        order.product_information.forEach((menu) => {
          const { menu_id = '-', menu_name, num, price } = menu;

          if (acc[menu_name]) {
            acc[menu_name].num += num; // 수량 합산
          } else {
            acc[menu_name] = {
              menu_id,
              menu_name,
              num,
              price,
            }; // 처음 추가
          }
        });
        return acc;
      }, {});

    const categoryMap = createCategoryMap(Categories?.data);
    const mappedData = Menus?.data?.map((menu) => {
      return {
        menu_id: menu?.menu_id,
        menu_name: menu?.menu_name,
        price: menu?.price,
        product_category_id: menu?.product_category_id,
        product_category_name: categoryMap?.[menu?.product_category_id] || '-',
      };
    });

    const sortedMenuArray =
      menuSales &&
      Object.entries(menuSales)
        .map(([menu_name, { menu_id, num, price }]) => {
          const categoryData = mappedData.find(
            (menu) => menu.menu_id === menu_id,
          );
          return {
            menu_name,
            menu_id,
            num,
            price,
            product_category_id: categoryData?.product_category_id || '-',
            product_category_name: categoryData?.product_category_name || '-',
          };
        })
        .sort((a, b) => b.num - a.num); // 수량을 기준으로 내림차순 정렬

    return sortedMenuArray;
  };

  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '', data = {}) => {
    setModalData((prev) => ({
      ...prev,
      [type]: data,
    }));
    setIsOpened(!open);
    setModalType(type);
  };

  /**
   * 좌표값 바인딩
   * --
   */
  const handleCoordChange = (lat, lng) => {
    setCoords({
      latitude: lat,
      longitude: lng,
    });
  };

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = (t, v, o, callback = null) => {
    let newData = modalData[o];

    newData[t] = v;
    const d = {
      ...modalData,
      o: { ...newData },
    };

    setModalData(d);
  };

  /**
   * 메뉴 삭제
   * --
   */
  const handleDeleteSelected = async (t = 'STORE', v) => {
    setIsLoading(true);
    try {
      const type = t.toLocaleUpperCase();
      const method = {
        STAFF: {
          DELETE: deleteStaff,
          VALUE: v,
          MESSAGE: '직원',
          ERROR_MESSAGE: '삭제를 실패했습니다. 다시 시도해주세요.',
        },
        STORE: {
          DELETE: updateShopInformation,
          VALUE: {
            shop_information_id: selectedStore?.shop_information_id,
            application_status: 'REJECT',
          },
          MESSAGE: '선택한 매장',
          ERROR_MESSAGE:
            '매장이 적용된 다른 데이터를 확인하고 다시 시도해주세요.',
        },
      };
      const { status } = await method[type].DELETE(method[type].VALUE);

      if (status === 200) {
        MessageAlert.success(`${method[type].MESSAGE}이 삭제처리 되었습니다.`);
        if (type === 'STORE') {
          refetch();
        } else {
          refetchStaffs();
        }
        setIsRefetch(true);
      } else {
        MessageAlert.error(method[type].ERROR_MESSAGE);
      }
    } catch (err) {
      MessageAlert.error('삭제를 실패 했습니다. 다시 시도해주세요');
      console.error('[DELETE EVENT ERROR]:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 데이터 생성
   * --
   * @param {string} t
   * @param {string} s
   * @returns
   */
  const handleSubmit = async (t, s = 'CREATE') => {
    // 생성 타입 대문자 변환
    const type = t.toLocaleUpperCase();
    // 함수 타입 대문자 변환
    const submit = s.toLocaleUpperCase();

    // 생성 타입 정의
    const submitType = {
      MENU: '메뉴',
      CATEGORY: '카테고리',
      STAFF: '직원',
      STORE: '매장',
      CREATE: '생성',
      UPDATE: '수정',
    };

    // console.log(t, s);
    // NOTE: 데이터 유효성 검사 및 데이터 리턴
    const data = handleValidateCheck(type);
    if (!data) return;

    // NOTE: 생성/수정 함수 정의
    const submitFunc = {
      MENU: {
        CREATE: createMenu,
        UPDATE: updateMenu,
      },
      CATEGORY: {
        CREATE: createProductCategory,
        UPDATE: updateProductCategory,
      },
      STAFF: {
        CREATE: createStaff,
        UPDATE: updateStaff,
      },
      STORE: {
        UPDATE: updateShopInformation,
      },
    };

    try {
      // 로딩 처리
      setIsLoading(true);

      // 데이터 생성/수정
      const { status } = await submitFunc[type][submit]({
        shop_information_id: selectedStore?.shop_information_id,
        ...data,
      });

      // 데이터 업데이트 성공 시
      if (status === 200 && t === 'store') {
        if (
          thumbnailFiles?.length > 0 ||
          menuFiles?.length > 0 ||
          storeFiles?.length > 0 ||
          licenseFiles?.length > 0 ||
          etcFiles?.length > 0
        ) {
          // 파일 리스트 정제
          const files = [
            ...thumbnailFiles.map((file) => {
              file.category = 'THUMBNAIL';
              return file;
            }),
            ...menuFiles.map((file) => {
              file.category = 'MENU';
              return file;
            }),
            ...storeFiles.map((file) => {
              file.category = 'STORE';
              return file;
            }),
            ...licenseFiles.map((file) => {
              file.category = 'LICENSE';
              return file;
            }),
            ...etcFiles.map((file) => {
              file.category = 'ETC';
              return file;
            }),
          ];
          const shop_information_id = selectedStore?.shop_information_id; // 생성된 매장 ID
          const fileSavePromises = files.map(async (file, index) => {
            const timestamp = Date.now();
            const fileExtension = file?.name?.split('.').pop();
            const baseName = file?.name?.replace(`.${fileExtension}`, '');
            const newBaseName = baseName?.replace(/[^a-zA-Z0-9_.-]/g, '_');
            const fileName = `jm_coffee/shop/${shop_information_id}/${newBaseName}_${timestamp}.${fileExtension}`;
            if (file.name) {
              // 각 파일을 개별적으로 업로드
              const uploadResponse = await fileUpload({
                file,
                file_name: fileName,
              });

              if (uploadResponse) {
                // 파일 정보 저장
                const shopFileInfo = {
                  shop_information_id,
                  file_name: `${newBaseName}_${timestamp}.${fileExtension}`,
                  file_url: fileName,
                  type: file.category,
                };
                return createShopInformationFile(shopFileInfo);
              }
            }
          });

          // 모든 파일 저장이 완료될 때까지 대기
          await Promise.all(fileSavePromises);
        }
        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`,
        );
        // 값 초기화
        setModalData((prev) => ({ ...prev, t: {} }));
        // 모달 닫기
        setIsOpened(!isOpened);
        // 리패치
        refetch();

        return;
      } else if (status === 200) {
        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`,
        );
        // 값 초기화
        setModalData((prev) => ({ ...prev, t: {} }));
        // 모달 닫기
        setIsOpened(!isOpened);
        // 리패치
        setIsRefetch(true);

        return;
      }

      MessageAlert.warning(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`,
      );
    } catch (err) {
      MessageAlert.error(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`,
      );
      console.error(`[CREATE ${type}] ERROR : `, err);
      throw err;
    } finally {
      // 로딩 처리
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 유효성 검사
   * --
   * @param {string} t
   * @returns
   */
  const handleValidateCheck = (t) => {
    if (t === 'MENU') {
    } else if (t === 'CATEGORY') {
    } else if (t === 'STORE') {
      // 지점 정보 유효성 검사
      const {
        shop_information_name,
        business_num,
        created_at,
        shop_information_address,
      } = modalData?.store || {};

      // 매장명 입력값 체크
      if (!shop_information_name) {
        MessageAlert.warning('매장명을 입력해주세요.');
        return false;
      }

      // 사업자등록번호 입력값 체크
      if (!business_num) {
        MessageAlert.warning('사업자등록번호를 입력해주세요.');
        return false;
      }

      // 주소 입력값 체크
      if (!shop_information_address) {
        MessageAlert.warning('주소를 입력해주세요.');
        return false;
      }

      // // 전화번호 입력값 체크 (11자리 이상이어야 함)
      // if (store_num && store_num.length < 11) {
      //   MessageAlert.warning('잘못된 전화번호 형식입니다.');
      //   return false;
      // }

      return {
        ...modalData?.store,
        ...coords,
        store_information: {
          store_owner: modalData?.store?.store_owner,
          business_hour: modalData?.store?.business_hour,
          business_num: modalData?.store?.business_num,
          store_num: modalData?.store?.store_num,
          services: {
            GROUP: modalData?.store?.GROUP,
            WIFI: modalData?.store?.WIFI,
            PARKING: modalData?.store?.PARKING,
            RESTROOM: modalData?.store?.RESTROOM,
            PACKAGING: modalData?.store?.PACKAGING,
            WHEELCHAIR: modalData?.store?.WHEELCHAIR,
          },
        },
        created_at: created_at ? Formatter.dateToUnix(created_at) : null,
      };
    } else if (t === 'STAFF') {
      // 직원 유효성 검사
      const { staff_name, staff_phone, birthday, hire_date, termination_date } =
        modalData?.staff;

      // 직원명 입력값 체크
      if (!staff_name) {
        MessageAlert.warning('직원명을 입력해주세요.');
        return false;
      }

      // 연락처 입력 시 유효성 검사
      if (staff_phone && staff_phone.length < 11) {
        MessageAlert.warning('잘못된 연락처 형식입니다.');
        return false;
      }

      return {
        ...modalData?.staff,
        birthday: birthday ? Formatter.dateToUnix(birthday) : null,
        hire_date: hire_date ? Formatter.dateToUnix(hire_date) : null,
        termination_date: termination_date
          ? Formatter.dateToUnix(termination_date)
          : null,
      };
    }
  };

  /* ===== Hooks ===== */
  /**
   * 데이터 정제
   * --
   */
  useEffect(() => {
    if (!Categories?.data || !Menus?.data || !ShopMenus?.data) return;

    const storeData = Stores?.data?.map((store) => {
      // 해당 매장의 shopMenus 필터링
      const filteredShopMenus = ShopMenus?.data?.filter(
        (shopmenu) =>
          shopmenu?.shop_information_id === store?.shop_information_id &&
          shopmenu?.is_show === 'Y',
      );
      // shopMenus에서 menu_id 목록 추출
      const filteredMenuIds = filteredShopMenus?.map(
        (shopmenu) => shopmenu.menu_id,
      );

      // 상위 카테고리 및 하위 카테고리 그룹화
      const categoryMap = Categories?.data?.reduce((acc, category) => {
        if (!category.parents_id) {
          acc[category.product_category_id] = {
            ...category,
            subCategories: [],
            menus: [], // 상위 카테고리의 메뉴 배열 초기화
          };
        } else {
          const parentCategory = acc[category.parents_id];
          if (parentCategory) {
            parentCategory.subCategories.push({
              ...category,
              menus: [], // 하위 카테고리의 메뉴 배열 초기화
            });
          }
        }
        return acc;
      }, {});

      // 메뉴를 해당 카테고리 또는 하위 카테고리에 매핑 (filteredMenuIds를 사용하여 필터링)
      Menus?.data
        ?.filter((menu) => filteredMenuIds.includes(menu.menu_id)) // menu_id가 filteredShopMenus에 있는지 필터링
        ?.forEach((menu) => {
          const { menu_file } = menu;

          const files = menu_file.length
            ? menu_file.reduce(
                (acc, cur) => {
                  const { type } = cur;
                  if (type === 'THUMBNAIL') {
                    acc['thumbnail'] = `${STORE_URL}/${cur.file_url}`;
                  } else {
                    acc['detail'].push(`${STORE_URL}/${cur.file_url}`);
                  }

                  return acc;
                },
                { thumbnail: null, detail: [] },
              )
            : { thumbnail: null, detail: [] };

          // const menuData = { ...menu, ...files };

          const category = categoryMap[menu?.product_category_id];
          if (category) {
            // 상위 카테고리의 메뉴 추가
            category?.menus?.push({ ...menu, ...files });
          } else {
            // 하위 카테고리의 메뉴 추가
            const parentCategory = Object?.values(categoryMap).find((c) =>
              c.subCategories?.some(
                (subCategory) =>
                  subCategory?.product_category_id ===
                  menu?.product_category_id,
              ),
            );
            if (parentCategory) {
              const subCategory = parentCategory?.subCategories?.find(
                (subCategory) =>
                  subCategory?.product_category_id ===
                  menu?.product_category_id,
              );
              if (subCategory) {
                subCategory?.menus?.push({ ...menu, ...files });
              }
            }
          }
        });
      // 카테고리 데이터를 배열로 변환하여 업데이트
      // 사용하지 않는 카테고리 제거
      const newData = Object.values(categoryMap)
        ?.filter(
          (item) =>
            item?.menus?.length ||
            (item?.subCategories?.length && item?.subCategories?.menus?.length),
        )
        ?.sort((a, b) => (a.order || 0) - (b.order || 0));

      // 하위 카테고리를 정렬
      Object.values(categoryMap).forEach((category) => {
        category.subCategories.sort((a, b) => (a.order || 0) - (b.order || 0));
      });

      // 각 매장의 데이터를 반환
      return {
        ...store,
        admin: Admins?.data?.find(
          (admin) => admin.shop_information_id === store.shop_information_id,
        ),
        menus: Menus?.data?.filter(
          (menu) => menu.shop_information_id === store.shop_information_id,
        ),
        staffs: Staffs?.data?.filter(
          (staff) => staff.shop_information_id === store.shop_information_id,
        ),
        shopMenus: newData, // 카테고리 구조에 맞게 정리된 shopMenus 데이터
        menuCount: ShopMenus?.data?.filter(
          (menu) => menu.shop_information_id === store.shop_information_id,
        )?.length,
      };
    });

    setData(storeData);
  }, [Stores, Menus, Admins, Staffs, Categories, ShopMenus]);

  /**
   * 카테고리 관련 데이터 정제
   * --
   */
  useEffect(() => {
    // 상위 카테고리 및 하위 카테고리 그룹화
    const categoryMap = Categories?.data?.reduce((acc, category) => {
      if (!category.parents_id) {
        acc[category.product_category_id] = {
          ...category,
          key: category?.product_category_id, // key 추가
          title: category?.product_category_name, // title 추가
          children: [],
          menus: [], // 상위 카테고리의 메뉴 배열 초기화
        };
      } else {
        const parentCategory = acc[category.parents_id];
        if (parentCategory) {
          parentCategory.children.push({
            ...category,
            key: category?.product_category_id, // key 추가
            title: category?.product_category_name, // title 추가
            menus: [], // 하위 카테고리의 메뉴 배열 초기화
          });
        }
      }
      return acc;
    }, {});
    // 카테고리 데이터를 배열로 변환하여 업데이트
    const newData = Object.values(categoryMap || [])?.sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    );

    setCategoryData(newData);
  }, []);
  /**
   * 탭 아이템 정제
   * --
   */
  useEffect(() => {
    const tabData = Stores?.data
      ?.filter((item) => item?.application_status !== 'REJECT')
      ?.map((store) => ({
        key: store.shop_information_id,
        label: store.shop_information_name,
        value: store.shop_information_id,
      }));

    setTabItems(tabData);
  }, [Stores]);

  /**
   * 오더 데이터 정제
   * --
   */
  useEffect(() => {
    const orders = Orders?.data?.filter(
      (item) =>
        item?.shop_information_id === selectedStore?.shop_information_id,
    );

    /* ===== ===== */
    let odd = {};
    let options = [];
    // const fff = orders.filter();
    if (Array.isArray(orders)) {
      for (let order of orders) {
        for (let pi of order.product_information) {
          if (pi.menu_option && pi.menu_option.length > 0) {
            // options.push(pi.menu_option);
            if (!odd[pi.menu_name]) {
              odd[pi.menu_name] = [];
            }
            odd[pi.menu_name].push(...pi.menu_option);
          }
        }
      }
    }

    /* === 메뉴별 옵션 집계 로직: START === */
    const rrr2 = Object.keys(odd).reduce((acc, item) => {
      // 메뉴생성
      if (!acc[item]) {
        acc[item] = { options: {} };
      }

      // 상품 > 옵션별 카운팅
      acc[item]['options'] = odd[item].reduce((acc2, item2) => {
        if (!acc2[item2.option_title]) {
          acc2[item2.option_title] = { price: 0, count: 0 };
        }

        // 옵션별 수량합계
        acc2[item2.option_title].count = item2.option.reduce(
          (acc3, item3) => (acc3 + item3.count === 0 ? 1 : item3.count),
          0,
        );
        // 옵션별 금액합계
        acc2[item2.option_title].price = item2.option.reduce(
          (acc3, item3) =>
            acc3 + item3.option_price * (item3.count === 0 ? 1 : item3.count),
          0,
        );

        return acc2;
      }, {});

      return acc;
    }, {});
    // console.log('rrr2: ', rrr2);
    /* === 메뉴별 옵션 집계 로직: END === */

    /* ===== ===== */

    const menuData = getSortedMenuData(orders);

    if (orders) {
      const { salesData, refundedData, couponDiscountData } = orders?.reduce(
        (acc, order) => {
          let date;
          // 선불매장
          if (
            order.shop_information_id !== 4 &&
            !order?.payment_info?.sale_date
          ) {
            date = moment.unix(order.created_at).format('YYYY-MM-DD');
          }
          // 후불매장 => 결제일과 영업일이 다른경우: [ 블랜디크(4) ]
          else {
            date = moment(order?.payment_info?.sale_date).format('YYYY-MM-DD');
          }

          if (order?.payment_state === 'COMPLETED') {
            acc.salesData[date] =
              (acc.salesData[date] || 0) + order.final_price;
          }

          if (order?.payment_state === 'REFUNDED') {
            acc.refundedData[date] =
              (acc.refundedData[date] || 0) + order.final_price;
          }
          if (order?.payment_state === 'COMPLETED') {
            if (order?.coupon_info && Array.isArray(order.coupon_info)) {
              const totalDiscount = order.coupon_info.reduce(
                (sum, coupon) => sum + (coupon.discount_price || 0),
                0,
              );
              acc.couponDiscountData[date] =
                (acc.couponDiscountData[date] || 0) + totalDiscount;
            }
          }

          return acc;
        },
        { salesData: {}, refundedData: {}, couponDiscountData: {} }, // 초기값 설정
      );
      setDailySales(salesData);
      setDailyRefundSales(refundedData);
      setDailyCouponSales(couponDiscountData);
    }

    setMenuSales(menuData);
    // eslint-disable-next-line
  }, [Orders, selectedStore, selectday]);

  // 파일데이터 정제
  useEffect(() => {
    const store = Stores?.data?.filter(
      (item) =>
        item?.shop_information_id === selectedStore?.shop_information_id,
    );
    // console.log('store', store);

    // 초기 값을 설정하고 각 파일 데이터를 그룹화
    const groupedFiles = store?.reduce(
      (acc, order) => {
        order?.shop_information_file?.forEach((file) => {
          if (file.type === 'THUMBNAIL') {
            acc.thumbnail.push(file);
          } else if (file.type === 'STORE') {
            acc.store.push(file);
          } else if (file.type === 'LICENSE') {
            acc.license.push(file);
          } else if (file.type === 'MENU') {
            acc.menu.push(file);
          } else {
            acc.etc.push(file);
          }
        });
        return acc;
      },
      { menu: [], thumbnail: [], store: [], license: [], etc: [] }, // 초기 값 설정
    );

    // 상태에 각각 저장
    setMenuFiles(groupedFiles?.menu);
    setThumbnailFiles(groupedFiles?.thumbnail);
    setStoreFiles(groupedFiles?.store);
    setLicenseFiles(groupedFiles?.license);
    setEtcFiles(groupedFiles?.etc);
  }, [Stores, selectedStore]);

  // console.log(data);
  // console.log(selectedStore);
  // console.log(menuSales);
  // console.log('selectday: ', selectday);

  /* ===== Render ===== */
  return (
    <>
      {isLoading && (
        <LoadingPage text={'데이터를 처리중입니다. 잠시만 기다려주세요.'} />
      )}
      <StoreListPresenter
        data={data}
        stores={Stores?.data}
        categoryData={categoryData}
        tabItems={tabItems}
        isOpened={isOpened}
        modalType={modalType}
        modalData={modalData}
        selectedStore={selectedStore}
        isRefetch={isRefetch}
        menuSales={menuSales}
        dailySales={dailySales}
        dailyRefundSales={dailyRefundSales}
        dailyCouponSales={dailyCouponSales}
        selectday={selectday}
        files={{
          menu: menuFiles,
          thumbnail: thumbnailFiles,
          store: storeFiles,
          license: licenseFiles,
          etc: etcFiles,
        }}
        navigate={navigate}
        onOpen={handleModal}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onDelete={handleDeleteSelected}
        onSelect={setSelectedStore}
        setIsRefetch={setIsRefetch}
        setSelectday={setSelectday}
        onCoordChange={handleCoordChange}
        onThumbnailFileChange={setThumbnailFiles}
        onMenuFileChange={setMenuFiles}
        onStoreFileChange={setStoreFiles}
        onLicenseFileChange={setLicenseFiles}
        onEtcFileChange={setEtcFiles}
      />
    </>
  );
};

export default StoreListContainer;
